<template>
  <div class="d-flex justify-space-between w-100 flex-column h-100">
    <div>
      <div class="ion-padding-bottom d-flex w-100 justify-space-center">
        <p class="font-md bold-medium blue-darker">{{ t('documents.addId') }}</p>
      </div>
      <p class="subtitle grey ion-padding-bottom">{{ t('documents.selectType') }}</p>

      <div class="content-document">
        <div class="block bg-white w-100">
          <ion-radio-group
            v-model="documentSelected"
            value="custom-checked"
            class="d-flex justify-space-between radio-documents"
            @update:model-value="onChange"
          >
            <ion-radio
              v-for="document in documents"
              :value="document.id"
              :key="`document-${document.id}`"
              >{{ t('profile.document.' + document.name) }}
            </ion-radio>
          </ion-radio-group>
        </div>

        <div v-if="documentSelected" class="mt-xs">
          <ion-row>
            <ion-col size="12">
              <section class="message-wrapper">
                <ion-icon :icon="informationCircleOutline" color="primary"></ion-icon>
                <ion-label class="text-color-placeholder text-xs text-start padding-list-item"
                  >{{ t('files.fileDescription') }}
                </ion-label>
              </section>
            </ion-col>
          </ion-row>

          <div v-for="(inp, idxInput) in getDocumentSelected?.Inputs" :key="`inp-${idxInput}`">
            <div v-if="inp.type === 'file' && inp.max" @click="inputSelected = inp">
              <UploadPhoto
                :file-type="inp.type"
                :label="inp.label"
                v-model="photo"
                :disabled="inp.Answers.length >= inp.max"
              />

              <div v-for="(imageAnswer, idxAnswer) in inp.Answers" :key="`answ-${idxAnswer}`">
                <ion-row
                  v-if="getDocumentSelected"
                  class="ba-grey pa-xs border-default document-card"
                >
                  <ion-col size="12" class="ion-no-padding d-flex justify-space-between">
                    <div class="d-flex">
                      <img class="border-default" :src="imageAnswer.value" />
                      <div class="pl-xs">
                        <p class="font-sm">{{ getDocumentSelected.name }} #{{ idxAnswer + 1 }}</p>
                        <p v-if="imageAnswer.size" class="font-sm grey text-start">
                          {{ (Number(imageAnswer.size) / 1024).toFixed(2) }} KB
                        </p>
                      </div>
                    </div>

                    <ion-button
                      @click="handleDeleteInput(idxInput, idxAnswer)"
                      color="primary"
                      shape="round"
                      size="small"
                      fill="clear"
                      class="ion-no-padding"
                    >
                      <ion-icon color="grey" :icon="trashBin"></ion-icon>
                    </ion-button>
                  </ion-col>
                </ion-row>
              </div>
            </div>
            <div v-if="inp.type === 'date'" class="mt-xs">
              <ion-input
                v-model="inp.Answers[0].value"
                type="date"
                mode="md"
                shape="round"
                class="custom-field"
                :label="t('profile.document.' + inp.label)"
                label-placement="stacked"
                fill="outline"
                :placeholder="t('profile.document.' + inp.label)"
              >
              </ion-input>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="d-flex justify-space-between mt-xs">
      <ion-button
        fill="clear"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="$emit('cancel')"
      >
        {{ t('actions.cancel') }}
      </ion-button>

      <ion-button
        :disabled="!getDisabled || isLoading"
        fill="solid"
        expand="block"
        size="default"
        color="primary"
        class="text-lg text-weight-bold"
        @click="handleCreateDocument"
      >
        {{ t('documents.addIdOn') }}
      </ion-button>
    </div>
    <ion-progress-bar v-if="isLoading" type="indeterminate" class="mt-xs"></ion-progress-bar>
  </div>
</template>

<script lang="ts" setup>
  import { useI18n } from 'vue-i18n';
  import { informationCircleOutline, trashBin } from 'ionicons/icons';
  import {
    IonButton,
    IonRadio,
    IonRadioGroup,
    IonRow,
    IonCol,
    IonIcon,
    IonProgressBar,
    IonLabel,
    IonInput,
  } from '@ionic/vue';
  import { storeToRefs } from 'pinia';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useToast } from '@/composables/useToast';
  import UploadPhoto from '../../shared/UploadPhoto.vue';
  import { IUploadImage } from '@/interfaces/IUploadImage';
  import { useProfileStore } from '@/stores/profile-store';
  import { IDocument } from '@/interfaces/profile/profile-document.interface';
  import { Answer } from '@/interfaces/tenants/documents.interface';
  import { DocumentForm } from '@/interfaces/profile/profile-response.interface';

  const documentSelected = ref();

  const { t } = useI18n();
  const emit = defineEmits(['continue', 'back', 'add-document', 'cancel']);
  const { showToast } = useToast();
  const { saveDocuments, deleteAnswerInput } = useProfileStore();
  const { documents } = storeToRefs(useProfileStore());
  const isLoading = ref(false);

  const inputSelected = ref();

  const photo = ref<IUploadImage>({
    base64: '',
    title: '',
    size: 0,
  });
  const transformAnswers = (data: IDocument) => {
    return data.Inputs.flatMap(({ Answers }) =>
      Answers.map(({ DocumentsMasterInputId, value, id }) => {
        const transformedObject: Answer = {
          DocumentsMasterInputId,
          value,
        };
        if (id !== undefined) {
          transformedObject.id = id;
        }
        return transformedObject;
      }),
    );
  };

  const handleCreateDocument = async () => {
    try {
      isLoading.value = true;

      const transformedArray = transformAnswers(getDocumentSelected.value as IDocument);

      const form: DocumentForm = {
        DocumentsMasterId: Number(getDocumentSelected.value?.id),
        answers: transformedArray,
      };

      const response = await saveDocuments(form);
      if (response.success) {
        emit('continue');
        await showToast(response.msg as string);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      isLoading.value = false;
    }
  };

  const setNewDocumentFile = () => {
    inputSelected.value.Answers.push({
      DocumentsMasterInputId: inputSelected.value.id,
      value: photo.value.base64,
      size: photo.value.size,
    });

    photo.value.base64 = '';
    photo.value.size = 0;
  };

  const onChange = () => {
    createAnswerObject();
  };

  const getDocumentSelected = computed(() => {
    return documents.value.find((document) => document.id === documentSelected.value);
  });

  const getDisabled = computed(() => {
    if (!getDocumentSelected.value) {
      return true;
    }
    let isValid = true;

    getDocumentSelected.value.Inputs.forEach(({ type, Answers, min, max }) => {
      if (type === 'date') {
        if (!Answers || Answers.length === 0 || Answers[0].value === '') {
          isValid = false;
        }
      } else if (type === 'file' && min && max) {
        if (Answers.length < min || Answers.length > max) {
          isValid = false;
        }
      }
    });

    return isValid;
  });

  const deleteAnswer = async (id: string, idxInput: number, idxAnswer: number) => {
    try {
      isLoading.value = true;
      const response = await deleteAnswerInput(id);
      if (response.success) {
        await showToast(response.msg as string);
        spliceDocument(idxInput, idxAnswer);
      } else {
        throw new Error(response.error);
      }
    } catch (error) {
      await showToast(error instanceof Error ? error.message : 'Unknown error', 'error');
    } finally {
      isLoading.value = false;
    }
  };

  const spliceDocument = (idxInput: number, idxAnswer: number) => {
    getDocumentSelected.value?.Inputs[idxInput].Answers.splice(idxAnswer, 1);
  };

  const handleDeleteInput = (idxInput: number, idxAnswer: number) => {
    if (getDocumentSelected.value?.Inputs[idxInput].Answers[idxAnswer].id) {
      deleteAnswer(
        getDocumentSelected.value?.Inputs[idxInput].Answers[idxAnswer].id,
        idxInput,
        idxAnswer,
      );
    } else {
      spliceDocument(idxInput, idxAnswer);
    }
  };

  const createAnswerObject = () => {
    const dateInputs = getDocumentSelected.value?.Inputs.filter((input) => input.type === 'date');

    if (dateInputs) {
      dateInputs.forEach((input) => {
        if (input.Answers.length === 0) {
          input.Answers.push({
            DocumentsMasterInputId: input.id,
            value: '',
          });
        } else if (!input.Answers[0].id) {
          input.Answers = [];
          input.Answers.push({
            DocumentsMasterInputId: input.id,
            value: '',
          });
        }
      });
    }
  };

  const initialConfig = () => {
    if (documents.value.length > 0) {
      documentSelected.value = documents.value[0].id;
      createAnswerObject();
    }
  };

  onMounted(() => {
    initialConfig();
  });

  watch(photo, (val) => {
    if (val.base64 !== '') {
      setNewDocumentFile();
    }
  });
</script>

<style lang="scss">
  .content-document {
    height: 380px;
    overflow: auto;
  }

  ion-radio {
    --border-radius: 4px;
    --inner-border-radius: 4px;

    --color: #ddd;
    --color-checked: var(--ion-color-primary);
  }

  ion-radio.ios::part(container) {
    width: 20px;
    height: 20px;

    border: 2px solid #ddd;
    border-radius: 4px;
  }

  .radio-checked.ios::part(container) {
    border-color: var(--ion-color-primary);
  }

  .radio-documents {
    flex-wrap: wrap;
    gap: 20px;
  }

  .document-card {
    img {
      width: 44px !important;
      object-fit: cover;
      height: 44px !important;
    }
  }
</style>
